import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { PagedDto } from '../models/paged.dto';

/**
 * The single customer read shape, mirroring the server's CustomerDto. Every
 * customer read endpoint returns a subset of these members chosen by the
 * request; an endpoint does not get its own interface because it carries a few
 * more or fewer columns. Only id and displayName are always present.
 */
export interface CustomerDto {
  id: number;
  displayName: string;
  address?: string | null;
  lat?: number | null;
  lng?: number | null;
  companyId?: number;
  accountNumber?: string | null;
  customerTypeId?: number | null;
  customerTypeName?: string | null;
  primaryContact?: string | null;
  phone?: string | null;
  ext?: string | null;
  mobilePhone?: string | null;
  email?: string | null;
  contact?: string | null;
  fax?: string | null;
  website?: string | null;
  isActive?: boolean;
  assigneeUserId?: number | null;
  assigneeName?: string | null;
  /** Active sub-accounts beneath this master, excluding itself. */
  childCount?: number;
  isMaster?: boolean;
  masterAccountId?: number | null;
  billingName?: string | null;
  billingAddressId?: number | null;
  billingCompanyName?: string | null;
  billingStatusId?: number | null;
  billingStatusName?: string | null;
  taxAreaId?: number | null;
  pricingAreaId?: number | null;
  paymentTermsId?: number | null;
  isTaxExempt?: boolean;
  taxExemptCertificateRef?: string | null;
  notes?: string | null;
  lostReason?: string | null;
  source?: string | null;
  commPrefId?: number | null;
  commPrefName?: string | null;
  serviceAddress?: string | null;
  serviceCity?: string | null;
  serviceState?: string | null;
  servicePostalCode?: string | null;
  billingAddress?: string | null;
  billingCity?: string | null;
  billingState?: string | null;
  billingPostalCode?: string | null;
  taxAreaName?: string | null;
  pricingAreaName?: string | null;
  paymentTermsName?: string | null;
  nextFollowUp?: string | null;
  lastActivityAt?: string | null;
  createdAt?: string;
  updatedAt?: string | null;
}

/** Server-side list parameters: the sidebar filters plus sort and paging. */
export interface CustomerListParams {
  /** Null/absent is the ordinary collection; [] is an empty map selection. */
  selectedIds?: readonly number[] | null;
  search?: string;
  customerTypeId?: number | null;
  sort?: string;
  desc?: boolean;
  page?: number;
  pageSize?: number;
  fields?: readonly string[];
  /** Filter-bar rules, each already spelled `field:op:value`. */
  rules?: readonly string[];
}

export interface CustomerEditRequest {
  displayName: string;
  customerTypeId: number | null;
  source: string | null;
  assigneeUserId: number | null;
  phone: string | null;
  ext: string | null;
  mobilePhone: string | null;
  email: string | null;
  contact: string | null;
  fax: string | null;
  website: string | null;
  notes: string | null;
}

export interface CustomerAddressDto {
  id: number;
  streetAddressLine1: string | null;
  streetAddressLine2: string | null;
  city: string | null;
  postalCode: string | null;
  stateId: number | null;
  countryId: number | null;
}

export interface CustomerProfileDto {
  customer: CustomerDto;
  serviceAddress: CustomerAddressDto | null;
  billingAddress: CustomerAddressDto | null;
}

export interface CustomerAddressInput {
  streetAddressLine1: string | null;
  streetAddressLine2: string | null;
  city: string | null;
  postalCode: string | null;
  stateId: number | null;
}

export interface CustomerAddressesRequest {
  service: CustomerAddressInput | null;
  billing: CustomerAddressInput | null;
}

export interface ConfigLookupDto {
  id: number;
  name: string;
  isActive: boolean;
  isSystem?: boolean;
}

export interface DeleteAllDataResult {
  deletedCount: number;
}

@Injectable({ providedIn: 'root' })
export class CustomerApiService {
  constructor(private http: HttpClient) {}

  // One server-side page of the customer list, matching the sidebar filters + sort.
  listCustomers(p: CustomerListParams): Observable<PagedDto<CustomerDto>> {
    let params = new HttpParams();
    if (p.search)                 params = params.set('search', p.search);
    if (p.customerTypeId != null) params = params.set('customerTypeId', p.customerTypeId);
    if (p.sort)                   params = params.set('sort', p.sort);
    if (p.desc != null)           params = params.set('desc', p.desc);
    if (p.page != null)           params = params.set('page', p.page);
    if (p.pageSize != null)       params = params.set('pageSize', p.pageSize);
    if (p.fields)                 params = params.set('fields', p.fields.join(','));
    // Appended, not set: `rule` repeats, one parameter per rule, and the
    // server ANDs them. Setting would leave only the last one filtering.
    for (const rule of p.rules ?? []) params = params.append('rule', rule);
    return p.selectedIds == null
      ? this.http.get<PagedDto<CustomerDto>>('/api/customers', { params })
      : this.http.post<PagedDto<CustomerDto>>('/api/customers/query', p.selectedIds, { params });
  }

  // Assign or clear (null) the current assignee.
  setAssignee(id: number, assigneeUserId: number | null): Observable<void> {
    return this.http.put<void>(`/api/customers/${id}/assignee`, { assigneeUserId });
  }

  searchCustomers(q: string): Observable<CustomerDto[]> {
    return this.http.get<CustomerDto[]>('/api/customers/search', {
      params: new HttpParams().set('q', q),
    });
  }

  getCustomerById(id: number): Observable<CustomerDto> {
    return this.http.get<CustomerDto>(`/api/customers/${id}`);
  }

  getCustomerProfile(id: number): Observable<CustomerProfileDto> {
    return this.http.get<CustomerProfileDto>(`/api/customers/${id}/profile`);
  }

  getCustomerRole(id: number): Observable<CustomerDto> {
    return this.http.get<CustomerDto>(`/api/customers/${id}/role`);
  }

  getCustomerChildren(masterId: number, query?: {
    search?: string; customerTypeId?: number | null; includeInactive?: boolean;
    sort?: string; desc?: boolean; fields?: readonly string[];
    rules?: readonly string[];
  }): Observable<CustomerDto[]> {
    let params = new HttpParams();
    if (query?.search) params = params.set('search', query.search);
    if (query?.customerTypeId != null) params = params.set('customerTypeId', query.customerTypeId);
    if (query?.includeInactive != null) params = params.set('includeInactive', query.includeInactive);
    if (query?.sort) params = params.set('sort', query.sort);
    if (query?.desc != null) params = params.set('desc', query.desc);
    if (query?.fields) params = params.set('fields', query.fields.join(','));
    // The rows under an expanded master answer the same filter the masters
    // did, so a child that contradicts the bar above it is never shown.
    for (const rule of query?.rules ?? []) params = params.append('rule', rule);
    return this.http.get<CustomerDto[]>(`/api/customers/${masterId}/children`, { params });
  }

  createMasterCustomer(dto: any): Observable<{ id: number }> {
    return this.http.post<{ id: number }>('/api/customers/master', dto);
  }

  updateCustomer(id: number, dto: any): Observable<void> {
    return this.http.put<void>(`/api/customers/${id}`, dto);
  }

  editCustomer(id: number, dto: CustomerEditRequest): Observable<void> {
    return this.http.put<void>(`/api/customers/${id}/editor`, dto);
  }

  updateCustomerAddresses(
    id: number,
    request: CustomerAddressesRequest,
  ): Observable<void> {
    return this.http.put<void>(`/api/customers/${id}/addresses`, request);
  }

  deactivateCustomer(id: number): Observable<void> {
    return this.http.delete<void>(`/api/customers/${id}`);
  }

  deleteAllCustomers(): Observable<DeleteAllDataResult> {
    return this.http.delete<DeleteAllDataResult>('/api/customers', {
      body: { confirmation: 'DELETE' },
    });
  }

  reactivateCustomer(id: number): Observable<void> {
    return this.http.put<void>(`/api/customers/${id}/reactivate`, {});
  }

  addChild(masterId: number, dto: any): Observable<{ id: number }> {
    return this.http.post<{ id: number }>(`/api/customers/${masterId}/children`, dto);
  }

  createAddress(dto: {
    streetAddressLine1?: string | null;
    streetAddressLine2?: string | null;
    city?: string | null;
    stateId?: number | null;
    postalCode?: string | null;
  }): Observable<{ id: number }> {
    return this.http.post<{ id: number }>('/api/addresses', dto);
  }

}
